import { Link } from "wouter";
import { GlassCard, PillTag, Avatar } from "./UI";
import Icon from "./Icon";
import { useStoreQuery, queries, type PingKind } from "@/lib/store";

/**
 * PartnerCard — a compact summary of the sister's active accountability
 * partnership for placement on /today. Shows partner avatar + name,
 * latest ping, today's check-in status, and a CTA to /sisters/partner.
 *
 * Renders nothing if there's no active pair, so it's safe to drop in.
 */
export default function PartnerCard() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account. Falling back to it
  // would show Hannah's accountability partner as if she were this sister's.
  const userId = me?.id ?? null;
  const pair = useStoreQuery(() => (userId ? queries.activePairForUser(userId) : null));
  const incoming = useStoreQuery(() => (userId ? queries.pendingInvitesForUser(userId) : []));

  // These two run unconditionally to keep hook order stable across renders;
  // they no-op until we have both a user and a pair.
  const partner = useStoreQuery(() =>
    userId && pair ? queries.partnerProfile(pair, userId) : null,
  );
  const nextRitual = useStoreQuery(() =>
    userId && pair ? queries.nextRitualForViewer(pair.id, userId) : null,
  );

  if (!userId) return null;

  // Show an invite-pending pill if there's an incoming invite and no active pair
  if (!pair && incoming.length > 0) {
    return <IncomingInvitePill />;
  }
  if (!pair) return null;
  const dayOfSeason = pair.startedAt
    ? Math.max(1, Math.floor((Date.now() - pair.startedAt) / (1000 * 60 * 60 * 24)) + 1)
    : 1;

  const subline = nextRitual ? nextRitual.label : "All in step today, sister";

  return (
    <Link href="/sisters/partner" className="no-underline block">
      <GlassCard organic padding={18}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", letterSpacing: "0.16em", fontSize: 10 }}>
            ACCOUNTABILITY · DAY {dayOfSeason}
          </p>
          <PillTag tone="forest">Walking</PillTag>
        </div>

        <div className="flex items-center gap-3">
          <div style={{ position: "relative" }}>
            <Avatar initials={partner?.initials ?? "S"} size={48} />
            <span
              style={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: 18,
                height: 18,
                borderRadius: 999,
                background: "var(--mnf-coral, #C0784A)",
                color: "#F5F0E8",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #F5F0E8",
              }}
              aria-hidden
            >
              <Icon name="favorite" size={10} />
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3
              className="mnf-serif"
              style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}
            >
              {partner?.name ?? "Your sister"}
            </h3>
            <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 2 }}>
              {subline}
            </p>
          </div>
          <Icon name="chevron_right" size={20} style={{ color: "var(--mnf-tg)" }} />
        </div>

        {nextRitual && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: 12,
              background: "rgba(192,120,74,0.08)",
              border: "1px solid rgba(192,120,74,0.18)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                background: "rgba(192,120,74,0.18)",
                color: "var(--mnf-coral, #C0784A)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-hidden
            >
              <Icon name={ritualIcon(nextRitual.kind)} size={15} />
            </span>
            <p
              className="mnf-body"
              style={{ flex: 1, fontSize: 12, color: "var(--mnf-fd)", lineHeight: 1.4 }}
            >
              {nextRitual.label}
            </p>
            <Icon name="chevron_right" size={16} style={{ color: "var(--mnf-tg)" }} />
          </div>
        )}
      </GlassCard>
    </Link>
  );
}

function IncomingInvitePill() {
  return (
    <Link href="/sisters/partner" className="no-underline block">
      <GlassCard
        organic
        padding={16}
        style={{
          background: "linear-gradient(135deg, rgba(192,120,74,0.12) 0%, rgba(245,240,232,0.85) 100%)",
          border: "1px solid rgba(192,120,74,0.25)",
        }}
      >
        <div className="flex items-center gap-3">
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: "var(--mnf-coral, #C0784A)",
              color: "#F5F0E8",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name="mail" size={18} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", fontSize: 10, letterSpacing: "0.16em" }}>
              AN INVITATION
            </p>
            <p
              className="mnf-serif"
              style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25, marginTop: 2 }}
            >
              A sister wants to walk with you.
            </p>
          </div>
          <Icon name="chevron_right" size={20} style={{ color: "var(--mnf-tg)" }} />
        </div>
      </GlassCard>
    </Link>
  );
}

function pingPhrase(kind: PingKind): string {
  if (kind === "praying") return "Praying for you now";
  if (kind === "won_morning") return "I won the morning";
  return "I'm struggling, sister";
}

function ritualIcon(kind: string): string {
  if (kind === "scripture") return "menu_book";
  if (kind === "cook_pick" || kind === "cook_mark") return "restaurant";
  if (kind === "cheer") return "favorite";
  return "forum";
}
